import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import {
  useListPortfolioSitesQuery,
  useListAdminSitesQuery,
  useLazyGetPortfolioSiteQuery,
} from '../redux/api/portfolioApi';
import { setCurrentSite } from '../redux/slices/siteSlice';
import type { PortfolioSite } from '../redux/api/portfolioApi';

interface SiteSelectorProps {
  onSiteChange?: (site: PortfolioSite | null) => void;
  className?: string;
}

const SiteSelector: React.FC<SiteSelectorProps> = ({ onSiteChange, className = '' }) => {
  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.auth.profile);
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);
  const organizationState = useAppSelector((s) => s.organization);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const userEmail = (profile.email || '').toLowerCase();
  const isSuperAdmin = userEmail.endsWith('@engramiq.com');
  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;

  // Super admin: fetch sites via /api/v1/admin/sites
  const adminArgs = isSuperAdmin
    ? (currentOrgId ? { organization_id: currentOrgId } : undefined)
    : undefined;

  const { data: adminSitesResponse, isLoading: adminSitesLoading } = useListAdminSitesQuery(adminArgs, {
    skip: !isSuperAdmin,
    // Rely on cache instead of refetching on every mount
  });

  // Other users: fetch sites for the selected portfolio
  const { data: portfolioSitesResponse, isLoading: portfolioSitesLoading } = useListPortfolioSitesQuery(
    selectedPortfolioId ? { portfolio_id: selectedPortfolioId } : { portfolio_id: '' },
    {
      skip: isSuperAdmin || !selectedPortfolioId,
      // Rely on cache instead of refetching on every mount
    }
  );

  // Use admin sites for super admin, portfolio sites for others
  const allSites = isSuperAdmin
    ? (Array.isArray(adminSitesResponse)
        ? adminSitesResponse
        : (adminSitesResponse as any)?.items || []) as PortfolioSite[]
    : (Array.isArray(portfolioSitesResponse)
        ? portfolioSitesResponse
        : (portfolioSitesResponse as any)?.items || []) as PortfolioSite[];

  const sitesLoading = isSuperAdmin ? adminSitesLoading : portfolioSitesLoading;
  const [triggerGetPortfolioSite] = useLazyGetPortfolioSiteQuery();

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSiteSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSiteSelect = async (site: PortfolioSite) => {
    const portfolioIdToUse = isSuperAdmin
      ? (site.portfolio_id || (site as any).portfolio_id)
      : selectedPortfolioId;

    if (!portfolioIdToUse) {
      console.error('No portfolio ID available for site');
      return;
    }

    try {
      const fullSite = await triggerGetPortfolioSite({
        portfolio_id: portfolioIdToUse,
        site_id: site.site_id,
      }).unwrap();

      dispatch(setCurrentSite(fullSite));
      onSiteChange?.(fullSite);
      setIsDropdownOpen(false);
      setSiteSearchTerm('');
    } catch (err) {
      console.error('Failed to fetch site details', err);
    }
  };

  const filteredSites = allSites.filter(
    (site) =>
      siteSearchTerm === '' ||
      site.site_name.toLowerCase().includes(siteSearchTerm.toLowerCase()) ||
      site.site_code?.toLowerCase().includes(siteSearchTerm.toLowerCase())
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="w-full px-3 py-2 pr-8 bg-[var(--color-input-bg)] hover:bg-[var(--color-surface-hover)] rounded-lg eg-text-primary text-sm text-left focus:outline-none transition-all truncate border border-[var(--color-border)]"
        disabled={sitesLoading || allSites.length === 0}
      >
        {currentSite?.site_name
          ? currentSite.site_name
          : sitesLoading
            ? 'Loading sites...'
            : allSites.length === 0
              ? 'No sites available'
              : 'Select site'}
      </button>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-engramiq-green pointer-events-none z-10" />

      {isDropdownOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-bg-primary)] rounded-lg shadow-lg z-50 max-h-64 overflow-hidden flex flex-col border border-[var(--color-border)]">
          <div className="p-2 border-b border-[var(--color-border)]">
            <input
              type="text"
              placeholder="Search sites..."
              value={siteSearchTerm}
              onChange={(e) => setSiteSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--color-input-bg)] rounded eg-text-primary text-sm focus:outline-none focus:border-engramiq-green border border-[var(--color-border)]"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="overflow-y-auto">
            {filteredSites.length === 0 ? (
              <div className="px-3 py-2 text-sm eg-text-secondary text-center">
                {siteSearchTerm ? 'No sites found' : 'No sites available'}
              </div>
            ) : (
              filteredSites.map((site) => (
                <button
                  key={site.site_id}
                  onClick={() => handleSiteSelect(site)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                    currentSite?.site_id === site.site_id
                      ? 'bg-engramiq-green/20 eg-text-primary'
                      : 'eg-text-primary'
                  }`}
                >
                  <div className="font-medium">{site.site_name}</div>
                  {site.site_code && (
                    <div className="text-xs eg-text-secondary">{site.site_code}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteSelector;
